import { useEffect, useRef } from "react"
import { useFocusWithin } from "rooks"

import { useChatContext } from "./components/ChatContext"

// Co-ordinates the focus of the chat input field.
// Returns refs that need to be passed to the chat input and its wrapper.
// Also returns a submitMessage function that involves focusing the chat input.
export const useMobileChatInput = () => {
  const { setIsChatOpen, isJoinModalOpen } = useChatContext()

  const inputRef = useRef<HTMLInputElement>(null)

  // useFocusWithin on its own is sufficient outside of Safari. Safari handles
  // button focus differently though: https://developer.mozilla.org/en-US/docs/Web/HTML/Element/button#clicking_and_focus
  // This presents a problem because it means that when a user clicks the button
  // to send a message, the focus moves to the <body> and then back to the <input>
  // If we immediately close on focus change then we will sometimes close the input
  // when in fact we want it to remain open. To avoid this issue, if focus moves out
  // of the wrapper, we set a timeout to close the input. If focus comes back, then
  // we cancel the timeout. If focus doesn't come back then we can safely close the input
  // because the user has intended for focus to move elsewhere. There is a balance here
  // between waiting long enough to ensure we give the browser time to move focus back
  // to the input, and waiting so long that the interaction seems sluggish for the user.
  // In some scenarios we know that focus won't come back, for example, when clicking outside
  // which is why there is an additional handler to immediately close on clicking outside.
  const focusTimeoutRef = useRef<number | null>(null)
  const { focusWithinProps: chatInputWrapperFocusProps } =
    useFocusWithin<HTMLDivElement>({
      onFocusWithinChange(isFocusWithin) {
        if (isFocusWithin && focusTimeoutRef.current !== null) {
          clearTimeout(focusTimeoutRef.current)
          focusTimeoutRef.current = null
        } else if (!isFocusWithin && !isJoinModalOpen) {
          focusTimeoutRef.current = window.setTimeout(() => {
            setIsChatOpen(false)
          }, 100)
        }
      },
    })

  useEffect(() => {
    if (!isJoinModalOpen) {
      inputRef.current?.focus()
    } else {
      setIsChatOpen(true)
    }
  }, [isJoinModalOpen])

  return {
    inputRef,
    chatInputWrapperFocusProps,
  }
}
